import clsx from 'clsx';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAddOnboarding, useAskChange, useDeclineChange, useEstimateChange, useParkChange, useStudioChanges, useStudioOnboarding, useToggleOnboarding } from '@/api/studio';
import type { ChangeRequest, InitiativeKind, OnboardingItem, Project } from '@/api/types';
import { StudioChangeDialog } from '@/components/ChangeDialog';
import { ViaApi } from '@/components/ViaApi';
import { QueryPending } from '@/components/QueryState';
import { Empty, Status } from '@/components/ui';
import { useStudioTerms, withSign } from '@/lib/contour';
import { formatMoney, formatWhen } from '@/lib/format';
import { answered, initiativeStatus, isOpen, kindLabel, urgencyLabel } from '@/lib/initiatives';

export function StudioOnboarding({ p }: { p: Project }) {
  const { data } = useStudioOnboarding(p.id);
  const toggle = useToggleOnboarding();
  const add = useAddOnboarding();
  const [kind, setKind] = useState<OnboardingItem['kind']>('access');
  const [title, setTitle] = useState('');
  const [hint, setHint] = useState('');
  const open = data?.filter((i) => !i.done).length ?? 0;

  return (
    <section className="max-w-3xl">
      <p className="mb-4 text-sm text-muted">Что клиент должен дать на старте. Клиент видит список в «Сейчас» с кнопками; пока он не закрыт, проект во «Внимании» у студии. Открыто: {open}.</p>
      <ul className="divide-y divide-line border-y border-line">
        {data?.map((i) => (
          <li key={i.id} className="flex items-center gap-3 py-2.5">
            <button onClick={() => toggle.mutate(i.id)} className={clsx('grid size-5 place-items-center rounded-sm', i.done ? 'bg-ink text-inverse' : 'ring-1 ring-strong')} aria-label={i.done ? 'Вернуть' : 'Отметить выполненным'}>
              {i.done && <Check className="size-3.5" strokeWidth={3} />}
            </button>
            <span className="flex-1 text-sm">
              {i.title}
              <span className="block text-xs text-muted">{i.hint}</span>
            </span>
            <span className="text-xs text-muted">{i.done ? `готово ${formatWhen(i.doneAt!)}` : 'ждём клиента'}</span>
          </li>
        ))}
      </ul>
      <form
        className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-[150px_1fr_1fr_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim()) return;
          add.mutate({ projectId: p.id, item: { kind, title: title.trim(), hint: hint.trim() } }, { onSuccess: () => (setTitle(''), setHint('')) });
        }}
      >
        <select className="field" value={kind} onChange={(e) => setKind(e.target.value as OnboardingItem['kind'])}>
          <option value="access">Доступ</option>
          <option value="materials">Материалы</option>
          <option value="contact">Контакт</option>
          <option value="meeting">Встреча</option>
        </select>
        <input className="field" placeholder="Что нужно" value={title} onChange={(e) => setTitle(e.target.value)} />
        <input className="field" placeholder="Подсказка клиенту" value={hint} onChange={(e) => setHint(e.target.value)} />
        <button className="btn-primary">Добавить</button>
      </form>
    </section>
  );
}

type Mode = 'take' | 'estimate' | 'ask' | 'park' | 'decline';

/** «Берём в работу»: a planned task in Linear right away, the direction picks its flow label. */
function Take({ c, onDone }: { c: ChangeRequest; onDone: () => void }) {
  const estimate = useEstimateChange();
  const [kind, setKind] = useState<InitiativeKind | ''>('');
  const [note, setNote] = useState('');
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[160px_1fr]">
      <select className="field" aria-label="Направление" value={kind} onChange={(e) => setKind(e.target.value as InitiativeKind | '')}>
        <option value="">Направление…</option>
        {(Object.keys(kindLabel) as InitiativeKind[]).map((k) => (
          <option key={k} value={k}>
            {kindLabel[k]}
          </option>
        ))}
      </select>
      <input className="field" aria-label="Решение студии" placeholder="Что сделаем и когда — увидит клиент" value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex gap-2 sm:col-span-2">
        <button className="btn-primary" disabled={estimate.isPending} onClick={() => estimate.mutate({ id: c.id, estimate: { inScope: true, kind: kind || undefined, note: note.trim() || undefined } }, { onSuccess: onDone })}>
          Взять и создать задачу
        </button>
      </div>
      <p className="text-xs text-muted sm:col-span-2">С задачи в Linear снимется метка client_inbox, клиент увидит её в «Задачах». Исполнителя, приоритет и метки ставьте в Linear.</p>
    </div>
  );
}

function Estimate({ c, onDone }: { c: ChangeRequest; onDone: () => void }) {
  const estimate = useEstimateChange();
  const { currency, sign } = useStudioTerms();
  const [price, setPrice] = useState('');
  const [days, setDays] = useState('5');
  const [note, setNote] = useState('');
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[140px_100px_1fr]">
      <input className="field num" placeholder={withSign('Цена', sign)} inputMode="numeric" value={price} onChange={(e) => setPrice(e.target.value.replace(/\D/g, ''))} />
      <input className="field num" placeholder="Дней" inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))} />
      <input className="field" placeholder="Что входит в оценку" value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex gap-2 sm:col-span-3">
        <button className="btn-primary" disabled={!Number(price) || estimate.isPending} onClick={() => estimate.mutate({ id: c.id, estimate: { price: { amountMinor: Number(price) * 100, currency }, days: Number(days) || 1, note } }, { onSuccess: onDone })}>
          Отправить оценку
        </button>
      </div>
    </div>
  );
}

/** One line of text and a button: the question, the parking note, the reason of a refusal. */
function TextAction({ label, placeholder, button, onSend, pending, error }: { label: string; placeholder: string; button: string; onSend: (text: string) => void; pending: boolean; error: Error | null }) {
  const [text, setText] = useState('');
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        <input className="field min-w-64 flex-1" aria-label={label} placeholder={placeholder} value={text} onChange={(e) => setText(e.target.value)} />
        <button className="btn-primary" disabled={!text.trim() || pending} onClick={() => onSend(text.trim())}>
          {button}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-sm text-overdue" role="alert">
          {error.message}
        </p>
      )}
    </div>
  );
}

const MODES: { mode: Mode; label: string }[] = [
  { mode: 'take', label: 'Берём в работу' },
  { mode: 'estimate', label: 'Оценить отдельно' },
  { mode: 'ask', label: 'Нужно уточнить' },
  { mode: 'park', label: 'Отложить' },
  { mode: 'decline', label: 'Не берём' },
];

/** The studio's answer to a new initiative: one of five, each with what the client will read. */
function Triage({ c }: { c: ChangeRequest }) {
  const [mode, setMode] = useState<Mode>();
  const ask = useAskChange();
  const park = useParkChange();
  const decline = useDeclineChange();
  const done = () => setMode(undefined);
  const modes = MODES.filter((m) => (m.mode === 'park' ? c.status !== 'parked' : m.mode === 'ask' ? c.status !== 'question' : true));
  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-1" role="group" aria-label="Ответ студии">
        {modes.map((m) => (
          <button key={m.mode} type="button" aria-pressed={mode === m.mode} className={clsx(mode === m.mode ? 'btn-primary' : 'btn-ghost')} onClick={() => setMode(mode === m.mode ? undefined : m.mode)}>
            {m.label}
          </button>
        ))}
      </div>
      {mode && (
        <div className="mt-2 rounded bg-panel p-3 shadow-hair">
          {mode === 'take' && <Take c={c} onDone={done} />}
          {mode === 'estimate' && <Estimate c={c} onDone={done} />}
          {mode === 'ask' && <TextAction label="Вопрос клиенту" placeholder="Что уточнить — клиент ответит в карточке" button="Спросить" pending={ask.isPending} error={ask.error} onSend={(text) => ask.mutate({ id: c.id, text }, { onSuccess: done })} />}
          {mode === 'park' && <TextAction label="Почему отложили" placeholder="Почему и когда вернёмся — увидит клиент" button="Отложить" pending={park.isPending} error={park.error} onSend={(note) => park.mutate({ id: c.id, note }, { onSuccess: done })} />}
          {mode === 'decline' && <TextAction label="Причина отказа" placeholder="Причина, её увидит клиент" button="Отклонить" pending={decline.isPending} error={decline.error} onSend={(reason) => decline.mutate({ id: c.id, reason }, { onSuccess: done })} />}
        </div>
      )}
    </div>
  );
}

/** An estimate the client has not answered yet can still be withdrawn. */
function Withdraw({ c }: { c: ChangeRequest }) {
  const decline = useDeclineChange();
  const [open, setOpen] = useState(false);
  if (!open)
    return (
      <button className="btn-ghost mt-2" onClick={() => setOpen(true)}>
        Отозвать оценку и отказать
      </button>
    );
  return (
    <div className="mt-2">
      <TextAction label="Причина отказа" placeholder="Причина, её увидит клиент" button="Отклонить" pending={decline.isPending} error={decline.error} onSend={(reason) => decline.mutate({ id: c.id, reason })} />
    </div>
  );
}

export function StudioChanges({ p }: { p: Project }) {
  const q = useStudioChanges(p.id);
  const data = q.data;
  const [params, setParams] = useSearchParams();
  const linked = params.get('open');
  const [picked, setShown] = useState<{ id: string; title: string }>();
  // A link from the Linear brief opens the card: ?open=<id>.
  const fromLink = linked ? data?.find((c) => c.id === linked) : undefined;
  const shown = picked ?? (fromLink && { id: fromLink.id, title: fromLink.title });
  const close = () => {
    setShown(undefined);
    if (linked) setParams((x) => (x.delete('open'), x), { replace: true });
  };
  if (!data) return <QueryPending query={q} />;
  if (!data.length) return <Empty title="Идей и запросов нет">Клиент присылает их из раздела «Идеи и запросы» своего проекта; каждая сразу попадает в Linear с меткой client_inbox.</Empty>;
  return (
    <>
      <ul className="max-w-3xl divide-y divide-line border-y border-line">
        {data.map((c) => (
          <li key={c.id} className="py-4">
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" className="text-left font-medium underline-offset-2 hover:underline" onClick={() => setShown({ id: c.id, title: c.title })}>
                {c.title}
              </button>
              <Status tone={c.status === 'new' ? 'overdue' : initiativeStatus[c.status].tone}>{answered(c) ? 'Клиент ответил' : initiativeStatus[c.status].studio}</Status>
              {c.urgency === 'urgent' && <span className="text-xs font-medium">Срочно</span>}
            </div>
            {c.goal && <p className="mt-1 text-sm">{c.goal}</p>}
            {c.description && <p className="mt-1 text-sm text-muted">{c.description}</p>}
            <p className="text-xs text-faint">
              {c.author.name}, {formatWhen(c.createdAt)}
              {c.urgency && c.urgency !== 'urgent' && ` · ${urgencyLabel[c.urgency]}`}
              {!!c.attachments?.length && ` · файлов: ${c.attachments.length}`}
              {c.estimate && `. Оценка: ${formatMoney(c.estimate.price)}, ${c.estimate.days} дн.`}
            </p>
            <ViaApi c={c} />
            {c.status === 'question' && c.question && <p className="mt-1 text-sm text-muted">Спросили: {c.question}</p>}
            {c.status === 'parked' && c.parkNote && <p className="mt-1 text-sm text-muted">Отложено: {c.parkNote}</p>}
            {c.declineReason && <p className="text-sm text-muted">Причина отказа: {c.declineReason}</p>}
            {c.task &&
              (c.taskUrl ? (
                <a href={c.taskUrl} target="_blank" rel="noreferrer" className="mt-1 inline-block text-sm underline underline-offset-2">
                  Задача {c.task.key}
                </a>
              ) : (
                <span className="mt-1 inline-block text-sm">Задача {c.task.key}</span>
              ))}
            {isOpen(c) && <Triage c={c} />}
            {c.status === 'estimated' && <Withdraw c={c} />}
          </li>
        ))}
      </ul>
      {shown && <StudioChangeDialog id={shown.id} title={shown.title} onClose={close} />}
    </>
  );
}
